import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { PrimeNGConfig } from 'primeng/api';
import { combineLatest, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { HttpHelpService } from '../service/http-help.service';
import { getDefaultReportLanguage } from 'src/app/shared/services/report-language-preference';

interface StatementInvoiceRow {
  is_account_entry?: boolean;
  entry_id?: number;
  invoice_id?: number;
  invoice_type?: number | string;
  invoice_type_name?: string;
  document_number?: string;
  invoice_date?: string;
  formatted_date?: string;
  formatted_debit_amount?: string | number;
  formatted_credit_amount?: string | number;
  running_balance?: string | number;
  formatted_running_balance?: string | number;
  invoice_note?: string;
  invoice_link?: string | null;
  has_pdf_link?: boolean;
}

interface DocumentTypeOption {
  code: string;
  labelKey: string;
}

@Component({
  selector: 'app-customer-account-card',
  templateUrl: './customer-account-card.component.html',
  styleUrls: ['./customer-account-card.component.scss'],
})
export class CustomerAccountCardComponent implements OnInit, OnDestroy {
  customerId: number | null = null;
  companyId: number | null = null;
  curentLang = 'ar';

  fromDate: Date | null = null;
  toDate: Date | null = null;
  dateRangeError = '';

  accountType = false;
  companyData: any[] = [];

  searchTerm = '';
  selectedDocumentTypeCodes: string[] = [];

  loading = false;
  loadError = '';

  customerInfo: any = null;
  accountSummary: any = null;
  statementRows: StatementInvoiceRow[] = [];
  statementInitialBalance: number = 0;

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  private readonly taxDocumentTypes: DocumentTypeOption[] = [
    { code: '305', labelKey: 'allDocuments.documentTypes.taxInvoice' },
    { code: '320', labelKey: 'allDocuments.documentTypes.invoiceWithReceipt' },
    { code: '330', labelKey: 'allDocuments.documentTypes.reverseInvoice' },
    { code: '400', labelKey: 'allDocuments.documentTypes.receipt' },
    { code: '430', labelKey: 'allDocuments.documentTypes.ReverseReceipt' },
    { code: '901', labelKey: 'customerBalances.statement.debitEntry' },
    { code: '902', labelKey: 'customerBalances.statement.creditEntry' },
  ];

  private readonly claimDocumentTypes: DocumentTypeOption[] = [
    { code: '300', labelKey: 'allDocuments.documentTypes.financialRequest' },
    { code: '325', labelKey: 'allDocuments.documentTypes.financialRequest' },
    { code: '332', labelKey: 'allDocuments.documentTypes.financialRequest' },
    { code: '400', labelKey: 'allDocuments.documentTypes.receipt' },
    { code: '430', labelKey: 'allDocuments.documentTypes.ReverseReceipt' },
    { code: '901', labelKey: 'customerBalances.statement.debitEntry' },
    { code: '902', labelKey: 'customerBalances.statement.creditEntry' },
  ];

  documentTypeOptions: { code: string; label: string }[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: HttpHelpService,
    private spinner: NgxSpinnerService,
    public translate: TranslateService,
    private primengConfig: PrimeNGConfig,
  ) {
    this.curentLang = localStorage.getItem('curentLang') || 'ar';
    this.initializeDateRange();
  }

  ngOnInit(): void {
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      this.curentLang = event.lang;
      this.applyCalendarLocale(event.lang);
      this.refreshDocumentTypeLabels();
    });

    this.curentLang = this.translate.currentLang || this.curentLang;
    this.applyCalendarLocale(this.curentLang);
    this.loadCompanyData();

    this.searchSubject.pipe(debounceTime(350), takeUntil(this.destroy$)).subscribe(() => {
      this.syncQueryParams();
    });

    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([params, query]) => {
        const id = Number(params.get('customerId'));
        this.customerId = Number.isFinite(id) ? id : null;
        this.applyRouteQuery(query);
        if (this.customerId && this.companyId) {
          this.loadStatement();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get documentTypesParam(): string {
    return this.selectedDocumentTypeCodes.filter(Boolean).join(',');
  }

  get customerDisplayName(): string {
    if (!this.customerInfo) {
      return '—';
    }
    if (this.curentLang === 'en') {
      return this.customerInfo.customer_name_en || this.customerInfo.customer_name_ar || '—';
    }
    if (this.curentLang === 'he') {
      return this.customerInfo.customer_name_he || this.customerInfo.customer_name_ar || '—';
    }
    return this.customerInfo.customer_name_ar || this.customerInfo.customer_name_en || '—';
  }

  get customerNumberDisplay(): string {
    return (
      this.customerInfo?.customer_id_number ||
      (this.customerInfo?.customer_id != null ? String(this.customerInfo.customer_id) : '—')
    );
  }

  initializeDateRange(): void {
    const year = new Date().getFullYear();
    this.fromDate = new Date(year, 0, 1);
    this.toDate = new Date(year, 11, 31);
  }

  private applyRouteQuery(q: ParamMap): void {
    const companyFromQuery = q.get('companyId');
    if (companyFromQuery) {
      this.companyId = Number(companyFromQuery);
    } else {
      const stored = localStorage.getItem('companyDefault');
      this.companyId = stored ? Number(stored) : null;
    }

    const start = q.get('startDate');
    const end = q.get('endDate');
    if (start) {
      const d = this.parseApiDate(start);
      if (d) {
        this.fromDate = d;
      }
    }
    if (end) {
      const d = this.parseApiDate(end);
      if (d) {
        this.toDate = d;
      }
    }

    const claim = q.get('claim');
    if (claim === 'true') {
      this.accountType = true;
    } else if (claim === 'false') {
      this.accountType = false;
    } else if (this.companyId) {
      this.accountType = this.getDefaultAccountType();
    }

    const docTypes = q.get('documentTypes');
    this.selectedDocumentTypeCodes = docTypes
      ? docTypes.split(',').map((c) => c.trim()).filter(Boolean)
      : [];

    this.searchTerm = q.get('search') || '';

    this.refreshDocumentTypeLabels();
  }

  private parseApiDate(iso: string): Date | null {
    const parts = iso.split('-');
    if (parts.length !== 3) {
      return null;
    }
    const d = new Date(+parts[0], +parts[1] - 1, +parts[2]);
    return isNaN(d.getTime()) ? null : d;
  }

  loadCompanyData(): void {
    this.service.getAllActiveCompanyByMerchant().subscribe({
      next: (res) => {
        if (Array.isArray(res)) {
          this.companyData = res;
        } else if (res?.data && Array.isArray(res.data)) {
          this.companyData = res.data;
        }
        if (!this.accountType && this.companyId) {
          this.accountType = this.getDefaultAccountType();
        }
      },
      error: () => {
        this.accountType = false;
      },
    });
  }

  getDefaultAccountType(): boolean {
    if (!this.companyId || !this.companyData?.length) {
      return false;
    }
    const selectedCompany = this.companyData.find((c: any) => c.company_id === this.companyId);
    const typeNameAr = selectedCompany?.companyType?.type_name_ar || '';
    if (typeNameAr.includes('جمعية') || typeNameAr.includes('معفي')) {
      return true;
    }
    return false;
  }

  refreshDocumentTypeLabels(): void {
    const source = this.accountType ? this.claimDocumentTypes : this.taxDocumentTypes;
    const seen = new Set<string>();
    this.documentTypeOptions = source
      .filter((opt) => {
        if (seen.has(opt.code)) {
          return false;
        }
        seen.add(opt.code);
        return true;
      })
      .map((opt) => ({
        code: opt.code,
        label: this.translate.instant(opt.labelKey),
      }));
  }

  onAccountTypeChange(): void {
    this.selectedDocumentTypeCodes = [];
    this.refreshDocumentTypeLabels();
    this.syncQueryParams();
  }

  onDateChange(): void {
    this.dateRangeError = '';
    if (this.fromDate && this.toDate && this.fromDate > this.toDate) {
      this.dateRangeError = this.translate.instant('customerAccountEntries.errors.invalidDateRange');
      return;
    }
    this.syncQueryParams();
  }

  onDocumentTypeChange(): void {
    this.syncQueryParams();
  }

  onSearchInput(): void {
    this.searchSubject.next(this.searchTerm);
  }

  syncQueryParams(): void {
    if (!this.customerId) {
      return;
    }
    const queryParams: Record<string, string | null> = {
      companyId: this.companyId != null ? String(this.companyId) : null,
      startDate: this.fromDate ? this.formatDate(this.fromDate) : null,
      endDate: this.toDate ? this.formatDate(this.toDate) : null,
      claim: this.accountType ? 'true' : 'false',
      documentTypes: this.documentTypesParam || null,
      search: this.searchTerm.trim() || null,
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  loadStatement(): void {
    if (!this.customerId || !this.companyId || !this.fromDate || !this.toDate) {
      return;
    }
    if (this.fromDate > this.toDate) {
      this.dateRangeError = this.translate.instant('customerAccountEntries.errors.invalidDateRange');
      return;
    }

    this.loading = true;
    this.loadError = '';
    this.spinner.show();

    const startDate = this.formatDate(this.fromDate);
    const endDate = this.formatDate(this.toDate);
    const lang = this.curentLang || getDefaultReportLanguage();

    this.service
      .getCustomerAccountSummary(
        this.customerId,
        this.companyId,
        startDate,
        endDate,
        lang,
        this.accountType,
        this.documentTypesParam || undefined,
        this.searchTerm.trim() || undefined,
      )
      .subscribe({
        next: (res) => {
          this.spinner.hide();
          this.loading = false;
          if (res?.status === 200 && res?.data) {
            const data = res.data;
            this.customerInfo = data.customer_info || null;
            this.accountSummary = data.account_summary || null;
            this.statementInitialBalance =
              data.account_summary?.initial_balance ??
              data.initial_balance ??
              data.opening_balance ??
              0;
            const invoices = data.invoices || data.formattedInvoices || [];
            this.statementRows = Array.isArray(invoices) ? invoices : [];
          } else {
            this.loadError =
              res?.messageAr ||
              res?.messageEn ||
              res?.messageHe ||
              this.translate.instant('customerBalances.statement.loadError');
          }
        },
        error: (err) => {
          this.spinner.hide();
          this.loading = false;
          this.loadError =
            this.curentLang === 'ar'
              ? err?.error?.messageAr
              : this.curentLang === 'he'
                ? err?.error?.messageHe
                : err?.error?.messageEn ||
                  this.translate.instant('customerBalances.statement.loadError');
        },
      });
  }

  exportPdf(): void {
    this.downloadBlobExport('pdf');
  }

  exportExcel(): void {
    this.downloadBlobExport('excel');
  }

  private downloadBlobExport(kind: 'pdf' | 'excel'): void {
    if (!this.customerId || !this.companyId || !this.fromDate || !this.toDate) {
      return;
    }
    this.spinner.show();
    const startDate = this.formatDate(this.fromDate);
    const endDate = this.formatDate(this.toDate);
    const lang = getDefaultReportLanguage();
    const filters = {
      documentTypes: this.documentTypesParam || undefined,
      search: this.searchTerm.trim() || undefined,
    };

    const request$ =
      kind === 'pdf'
        ? this.service.getCustomerAccountStatementPdf(
            this.customerId,
            this.companyId,
            startDate,
            endDate,
            lang,
            this.accountType,
            filters.documentTypes,
            filters.search,
          )
        : this.service.getCustomerAccountStatementExcel(
            this.customerId,
            this.companyId,
            startDate,
            endDate,
            lang,
            this.accountType,
            filters.documentTypes,
            filters.search,
          );

    request$.subscribe({
      next: (blob: Blob) => {
        this.spinner.hide();
        const url = window.URL.createObjectURL(blob);
        const ext = kind === 'pdf' ? 'pdf' : 'xlsx';
        const link = document.createElement('a');
        link.href = url;
        link.download = `customer_account_${this.customerId}_${startDate}_${endDate}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 200);
      },
      error: () => {
        this.spinner.hide();
      },
    });
  }

  goBackToCustomers(): void {
    this.router.navigate(['/dashboard/customers']);
  }

  formatDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  formatDisplayDate(d: Date | null): string {
    if (!d) {
      return '—';
    }
    const day = String(d.getDate()).padStart(2, '0');
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}/${m}/${d.getFullYear()}`;
  }

  formatBalance(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(Number(value))) {
      return '—';
    }
    return Number(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  formatStatementAmount(value: string | number | null | undefined, treatZeroAsDash = true): string {
    if (value == null || value === '') {
      return '—';
    }
    const num = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
    if (!Number.isFinite(num)) {
      return String(value);
    }
    if (treatZeroAsDash && num === 0) {
      return '—';
    }
    return this.formatBalance(num);
  }

  isAccountEntryRow(row: StatementInvoiceRow): boolean {
    return row?.is_account_entry === true;
  }

  canOpenRowPdf(row: StatementInvoiceRow): boolean {
    if (this.isAccountEntryRow(row)) {
      return false;
    }
    if (row.has_pdf_link === false) {
      return false;
    }
    return !!(row.invoice_link && String(row.invoice_link).trim());
  }

  openRowPdf(row: StatementInvoiceRow, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.canOpenRowPdf(row) || !row.invoice_link) {
      return;
    }
    window.open(row.invoice_link, '_blank');
  }

  getStatementRowTypeLabel(row: StatementInvoiceRow): string {
    if (row.invoice_type_name) {
      return String(row.invoice_type_name);
    }
    if (this.isAccountEntryRow(row)) {
      const type = Number(row.invoice_type);
      if (type === 901) {
        return this.translate.instant('customerBalances.statement.debitEntry');
      }
      if (type === 902) {
        return this.translate.instant('customerBalances.statement.creditEntry');
      }
      return this.translate.instant('customerBalances.statement.accountEntry');
    }
    return row.document_number || '—';
  }

  getMaxFromDate(): Date | undefined {
    return this.toDate || undefined;
  }

  getMinToDate(): Date | undefined {
    return this.fromDate || undefined;
  }

  private applyCalendarLocale(lang: string): void {
    if (lang === 'ar') {
      this.primengConfig.setTranslation({
        dayNames: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
        dayNamesShort: ['أحد', 'إثن', 'ثلا', 'أرب', 'خمي', 'جمع', 'سبت'],
        dayNamesMin: ['ح', 'ن', 'ث', 'ر', 'خ', 'ج', 'س'],
        monthNames: [
          'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
          'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
        ],
        monthNamesShort: [
          'ينا', 'فبر', 'مار', 'أبر', 'ماي', 'يون',
          'يول', 'أغس', 'سبت', 'أكت', 'نوف', 'ديس',
        ],
        today: 'اليوم',
        clear: 'مسح',
        weekHeader: 'أسبوع',
        firstDayOfWeek: 6,
        dateFormat: 'dd/mm/yy',
      });
      return;
    }
    if (lang === 'he') {
      this.primengConfig.setTranslation({
        dayNames: ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'],
        dayNamesShort: ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'],
        dayNamesMin: ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'],
        monthNames: [
          'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
          'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
        ],
        monthNamesShort: [
          'ינו', 'פבר', 'מרץ', 'אפר', 'מאי', 'יונ',
          'יול', 'אוג', 'ספט', 'אוק', 'נוב', 'דצמ',
        ],
        today: 'היום',
        clear: 'נקה',
        weekHeader: 'שבוע',
        firstDayOfWeek: 0,
        dateFormat: 'dd/mm/yy',
      });
      return;
    }
    this.primengConfig.setTranslation({
      dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
      monthNames: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
      ],
      monthNamesShort: [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
      ],
      today: 'Today',
      clear: 'Clear',
      weekHeader: 'Wk',
      firstDayOfWeek: 0,
      dateFormat: 'dd/mm/yy',
    });
  }
}
